import { Localized, useLocalization } from "@fluent/react"
import { useEffect, useMemo, useState } from "react"
import { Button, ButtonGroup, Fab, Paper, TableContainer } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import AddIcon from '@mui/icons-material/Add'
import { Link, useNavigate } from 'react-router-dom'
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { ProjectStatusChip } from "../Components/ProjectStatusChip"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { selectSettings } from "../../settings/settingsSlice"
import { loadArchivedProjects } from "./archivedProjectsApi"
import { ProjectHeader } from "../projectSlice"
import { unwrapResult } from "@reduxjs/toolkit"
import { enUS, plPL } from "@mui/x-data-grid/locales"

export const ArchivedProjectsList = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [projects, setProjects] = useState<ProjectHeader[]>([]);
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);


    useEffect(() => {
        setIsLoading(true);
        dispatch(loadArchivedProjects())
            .then(unwrapResult)
            .then(setProjects)
            .catch((error) => { console.log(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleGoToClick = (id) => {
        navigate(`/archived-projects/view/${id}`)
    };

    const columns: GridColDef[] =
        [
            {
                field: 'no',
                width: 200,
                headerName: l10n.getString('project-no', null, 'Numer projektu'),

            },
            {
                field: 'name',
                headerName: l10n.getString('project-title', null, 'Tytuł projektu'),
                flex: 1
            },
            {
                field: 'companyName',
                headerName: l10n.getString('project-company-name', null, 'Nazwa firmy'),
                width: 400
            },
            {
                field: 'archivedAt',
                headerName: l10n.getString('archive-date', null, '_Data archiwizacji'),
                width: 150,
                type: 'date',

                valueGetter: (_, row) =>
                    row.archivedAt ? new Date(row.archivedAt) : null,
            },
            {
                field: 'archivedBy',
                headerName: l10n.getString('archived-by', null, '_Zarchiwizowane przez'),
                width: 150
            },
            {
                field: 'isArchived',
                headerName: l10n.getString('archived', null, '_archived'),
                width: 150,
                type: 'boolean',
            },
            {
                field: 'actions', type: 'actions', width: 200,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button onClick={() => handleGoToClick(params.id)} color="secondary" variant="outlined">
                                <Localized id="go-to">
                                    <span>Przejdź</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];

    if (error) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>

            <TableContainer component={Paper}>
                <DataGrid
                    localeText={getCustomLocaleText()}
                    rows={projects}
                    columns={columns}
                    style={{ minHeight: 100, height: 'calc(100vh - 150px)' }}
                    slots={{ toolbar: QuickSearchToolbar }}
                    onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                    initialState={{
                        sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                    }}
                />
            </TableContainer>
        </>
    )
}
